// Formatos es-AR mínimos que necesitan las primitivas (DESIGN_SYSTEM §3.2).
// Entre el símbolo y el número va un espacio duro (U+00A0), igual que fmtARS de @atte/domain: así "$" o "−$"
// nunca quedan solos al final de una línea.

const MINUS = '−';
const NNBSP = ' ';
const NBSP = ' ';

const intFmt = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const oneDecimal = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 });

/** "$ 12.500" (pesos enteros). null → "—". Negativo con "−" (U+2212). */
export function formatMoney(value: number | null | undefined, currencyLabel = '$'): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = intFmt.format(Math.abs(Math.round(value)));
  return `${value < 0 ? MINUS : ''}${currencyLabel}${NBSP}${abs}`;
}

/** "+$ 12.500" / "−$ 12.500" / "$ 0". */
export function formatSigned(value: number | null | undefined, currencyLabel = '$'): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value === 0) return `${currencyLabel}${NBSP}0`;
  return `${value > 0 ? '+' : MINUS}${currencyLabel}${NBSP}${intFmt.format(Math.abs(Math.round(value)))}`;
}

/** Entero con separador de miles: "1.284". */
export function formatInt(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value < 0 ? MINUS : ''}${intFmt.format(Math.abs(Math.round(value)))}`;
}

/** Monto sin símbolo para campos de texto: "12.500". */
export function formatAmountPlain(value: number): string {
  return `${value < 0 ? '-' : ''}${intFmt.format(Math.abs(value))}`;
}

/** "3,5 %" con espacio fino; "—" si no es finito. */
export function formatPct(value: number | null | undefined, decimals: 0 | 1 = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const f = decimals === 0 ? intFmt : pctFmt;
  return `${value < 0 ? MINUS : ''}${f.format(Math.abs(value))}${NNBSP}%`;
}

/** Variación: "+12,3 %" / "−4,0 %" / "±0,0 %"; en puntos porcentuales "+2,1 pp". */
export function formatDelta(value: number, unit: 'pct' | 'pp' = 'pct'): string {
  const sign = value > 0 ? '+' : value < 0 ? MINUS : '±';
  return `${sign}${pctFmt.format(Math.abs(value))}${NNBSP}${unit === 'pct' ? '%' : 'pp'}`;
}

/** Compacto para ejes: "$ 1,2 M", "$ 850 mil", "$ 900". */
export function formatMoneyCompact(value: number, currencyLabel = '$'): string {
  const sign = value < 0 ? MINUS : '';
  const a = Math.abs(value);
  if (a >= 1_000_000) return `${sign}${currencyLabel}${NBSP}${oneDecimal.format(a / 1_000_000)}${NBSP}M`;
  if (a >= 1_000) return `${sign}${currencyLabel}${NBSP}${intFmt.format(Math.round(a / 1_000))}${NBSP}mil`;
  return `${sign}${currencyLabel}${NBSP}${intFmt.format(a)}`;
}

/** Fecha corta "2/11" desde YYYY-MM-DD. */
export function formatDayMonth(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${Number(d)}/${Number(m)}`;
}

/** Plural es-AR explícito: plural(3, 'turno', 'turnos') → "3 turnos". */
export function plural(n: number, one: string, many: string): string {
  const rule = new Intl.PluralRules('es-AR').select(n);
  return `${formatInt(n)} ${rule === 'one' ? one : many}`;
}
